import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Coins, BadgeCheck, Bell, Target, X, Eye } from "lucide-react";
import { LEVELS } from "@/lib/levels";
import { ShareWin } from "@/components/ShareWin";
import { useCountUp } from "@/components/PointsBurst";
import { SheenBar } from "@/components/home/HomeHero";
import { useAuth } from "@/context/AuthContext";
import { wearFrame, useLevelFrame } from "@/lib/levelFrames";
import { FrameAvatar } from "@/components/LevelFramePicker";
import { toast } from "sonner";
import { formatErr } from "@/lib/api";

const spring = { type: "spring", stiffness: 260, damping: 18 };
const fmt = (n) => Number(n || 0).toLocaleString("de-DE");

// up: { level, name, emoji, color, next, bonus, points, from }
const perksFor = (up) => [
  [Coins, `+${fmt(up.bonus)} Bonuspunkte`, "Schon auf deinem Konto gutgeschrieben.", "from-amber-300 to-amber-500", "bonus"],
  [BadgeCheck, `Neuer Titel „${up.name}“ ${up.emoji}`, "Sichtbar an deinem Profil, deinen Beiträgen und in den Ranglisten.", "from-pink-400 to-rose-600", "title"],
  [Bell, "Die Community feiert mit", "Dein Aufstieg erscheint bei den anderen Mitgliedern in den Mitteilungen.", "from-fuchsia-400 to-pink-600", "community"],
  ...(up.next ? [[Target, `Nächstes Ziel: Level ${up.level + 1} ${up.next.emoji}`, `„${up.next.name}“ ab ${fmt(up.next.min)} Punkten – wieder mit Bonus.`, "from-rose-400 to-orange-400", "next"]] : []),
];

function FramePerk({ up, preview, delay, reduce }) {
  const { user } = useAuth();
  const worn = useLevelFrame(user?.user_id);
  const [busy, setBusy] = useState(false);
  const on = worn === up.level;
  const wear = async (e) => {
    e.stopPropagation();
    if (preview) { toast.info("Vorschau – hier wird nichts geändert"); return; }
    setBusy(true);
    try { await wearFrame(user.user_id, up.level); toast.success("Neuer Rahmen angelegt ✨"); } catch (err) { toast.error(formatErr(err)); }
    setBusy(false);
  };
  return (
    <motion.div initial={reduce ? false : { opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ ...spring, delay }}
      className="flex items-center gap-3 p-3 rounded-2xl bg-white/10 ring-1 ring-amber-200/40 backdrop-blur-md" data-testid="levelup-perk-frame">
      <FrameAvatar level={up.level} name={user?.name} picture={user?.picture} size="w-12 h-12" />
      <span className="min-w-0 flex-1"><span className="block font-extrabold leading-tight">Neuer Profilrahmen</span><span className="block text-xs text-white/75 mt-0.5">„{up.name}“ – wähle ihn jederzeit unter Profil bearbeiten.</span></span>
      <button type="button" onClick={wear} disabled={busy || on} data-testid="levelup-wear-frame" className="tap shrink-0 px-3 h-9 rounded-full bg-amber-300 text-amber-950 text-xs font-extrabold disabled:opacity-70">{on ? "Getragen ✓" : busy ? "…" : "Jetzt tragen"}</button>
    </motion.div>
  );
}

function Rays() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
      <div className="lvl-rays absolute left-1/2 top-[30%] w-[160vmax] h-[160vmax] -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute left-1/2 top-[30%] w-[70vmin] h-[70vmin] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(253,230,138,.55),rgba(244,63,126,.15)_45%,transparent_70%)]" />
      {Array.from({ length: 18 }, (_, i) => <span key={i} className="lvl-spark" style={{ left: `${(i * 37) % 100}%`, animationDelay: `${(i % 6) * 0.45}s`, animationDuration: `${3.5 + (i % 4)}s` }} />)}
    </div>
  );
}

function Medal({ up, reduce }) {
  const n = useCountUp(up.level, 900);
  return (
    <motion.div initial={reduce ? false : { scale: 0, rotate: -160 }} animate={{ scale: 1, rotate: 0 }} transition={{ ...spring, delay: 0.25 }}
      className="relative mx-auto w-40 h-40 sm:w-44 sm:h-44" data-testid="levelup-medal">
      <span className="absolute inset-0 rounded-full lvl-ring" />
      <span className={`absolute inset-3 rounded-full bg-gradient-to-br ${up.color} lvl-medal-face flex flex-col items-center justify-center`}>
        <span className="text-5xl leading-none drop-shadow">{up.emoji}</span>
        <span className="font-display text-3xl font-black text-white tabular-nums drop-shadow" data-testid="level-up-number">Lv. {n}</span>
      </span>
      <span className="absolute inset-3 rounded-full lvl-medal-shine" />
    </motion.div>
  );
}

export function LevelUpShow({ up, onClose, preview = false }) {
  const reduce = useReducedMotion();
  const [step, setStep] = useState(reduce ? 2 : 0);
  useEffect(() => {
    if (reduce) return undefined;
    const t1 = setTimeout(() => setStep(1), 900), t2 = setTimeout(() => setStep(2), 1700);
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => { clearTimeout(t1); clearTimeout(t2); window.removeEventListener("keydown", esc); };
  }, [reduce, onClose]);
  const skip = () => setStep(2);
  const pct = up.next ? ((up.points - LEVELS[up.level - 1].min) / (up.next.min - LEVELS[up.level - 1].min)) * 100 : 100;
  return createPortal(
    <div className="fixed inset-0 z-[94] overflow-y-auto lvl-backdrop" data-testid="level-up-modal" role="dialog" aria-modal="true" aria-label={`Level ${up.level} erreicht`} onClick={skip} style={{ pointerEvents: "auto" }}>
      <Rays />
      {preview && <span className="fixed top-4 left-4 z-10 inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-amber-300 text-amber-950 text-xs font-extrabold shadow-lg" data-testid="levelup-preview-badge"><Eye className="w-3.5 h-3.5" /> VORSCHAU – nur für dich, keine Punkte</span>}
      <button type="button" onClick={(e) => { e.stopPropagation(); onClose(); }} aria-label="Schließen" data-testid="levelup-close-x" className="fixed top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/15 text-white backdrop-blur flex items-center justify-center hover:bg-white/25"><X className="w-5 h-5" /></button>
      <div className="relative min-h-full flex flex-col items-center justify-center px-5 py-16 text-center text-white">
        <motion.p initial={reduce ? false : { opacity: 0, y: -12, letterSpacing: "0.6em" }} animate={{ opacity: 1, y: 0, letterSpacing: "0.35em" }} transition={{ duration: 0.7 }}
          className="text-xs font-extrabold uppercase text-amber-200">Level-Aufstieg</motion.p>
        <div className="mt-5"><Medal up={up} reduce={reduce} /></div>
        <motion.h2 initial={reduce ? false : { opacity: 0, scale: 0.8, filter: "blur(8px)" }} animate={step >= 1 ? { opacity: 1, scale: 1, filter: "blur(0px)" } : {}} transition={spring}
          className="mt-6 font-display text-4xl sm:text-5xl font-black lvl-title" data-testid="level-up-name">LEVEL {up.level} ERREICHT!</motion.h2>
        <motion.p initial={reduce ? false : { opacity: 0 }} animate={step >= 1 ? { opacity: 1 } : {}} transition={{ delay: 0.15 }} className="mt-2 text-base font-semibold text-white/90">
          Du bist jetzt <span className="font-extrabold text-amber-200">{up.emoji} {up.name}</span>
        </motion.p>
        {step >= 2 && (
          <div className="mt-7 w-full max-w-md space-y-2.5 text-left" data-testid="levelup-perks" onClick={(e) => e.stopPropagation()}>
            <p className="text-[11px] font-extrabold uppercase tracking-[0.25em] text-white/70 text-center">Neu freigeschaltet</p>
            {perksFor(up).map(([Icon, title, text, grad, id], i) => [
              <motion.div key={id} initial={reduce ? false : { opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ ...spring, delay: (i ? i + 1 : 0) * 0.12 }}
                className="flex items-center gap-3 p-3 rounded-2xl bg-white/10 ring-1 ring-white/20 backdrop-blur-md" data-testid={`levelup-perk-${id}`}>
                <span className={`w-11 h-11 rounded-xl bg-gradient-to-br ${grad} flex items-center justify-center shrink-0 shadow-lg`}><Icon className="w-5 h-5 text-white" /></span>
                <span className="min-w-0"><span className="block font-extrabold leading-tight">{title}</span><span className="block text-xs text-white/75 mt-0.5">{text}</span></span>
              </motion.div>,
              i === 0 && <FramePerk key="frame" up={up} preview={preview} delay={0.12} reduce={reduce} />,
            ])}
            {up.next && (
              <motion.div initial={reduce ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="pt-1" data-testid="levelup-next-progress">
                <SheenBar pct={pct} className="h-2" track="bg-white/15" />
                <p className="mt-1 text-[11px] font-bold text-white/75 text-center">{fmt(up.points)} / {fmt(up.next.min)} Punkte bis Level {up.level + 1}</p>
              </motion.div>
            )}
            <motion.div initial={reduce ? false : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75 }} className="pt-3 flex flex-col items-center gap-3">
              {!preview && <ShareWin dark text={`Ich habe in der itzPlay Community Level ${up.level} „${up.name}“ ${up.emoji} erreicht!`} testId="level-up-share" />}
              <button type="button" onClick={onClose} data-testid="level-up-close" className="tap w-full max-w-xs h-13 py-3.5 rounded-full bg-white text-rose-600 font-extrabold text-lg shadow-[0_12px_30px_-8px_rgba(255,255,255,.6)]">Weiter geht's</button>
            </motion.div>
          </div>
        )}
        {step < 2 && <p className="absolute bottom-6 inset-x-0 text-xs font-semibold text-white/60" data-testid="levelup-skip-hint">Tippen zum Überspringen</p>}
      </div>
    </div>,
    document.body,
  );
}
